import { HttpRequest } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { CsAgentAuthService } from '@spartacus/asm/root'
import {
  AuthRedirectService,
  AuthService,
  AuthStorageService,
  AuthToken,
  GlobalMessageService,
  GlobalMessageType,
  InterceptorUtil,
  OAuthLibWrapperService,
  OccEndpointsService,
  RoutingService,
  USE_CUSTOMER_SUPPORT_AGENT_TOKEN,
  WindowRef
} from '@spartacus/core'
import { CookieService } from 'ngx-cookie-service'
import { take } from 'rxjs/operators'
import { EbLocalStorageKey } from '../../../configuration/models/eb-local-storage.model'
import { EbAuthHttpHeaderService } from '../../auth/services/eb-auth-http-header.service'

@Injectable({
  providedIn: 'root'
})
export class EbAsmAuthHttpHeaderService extends EbAuthHttpHeaderService {
  constructor(
    authService: AuthService,
    authStorageService: AuthStorageService,
    oAuthLibWrapperService: OAuthLibWrapperService,
    routingService: RoutingService,
    occEndpoints: OccEndpointsService,
    protected globalMessageService: GlobalMessageService,
    authRedirectService: AuthRedirectService,
    protected cookieService: CookieService,
    protected router: Router,
    protected winRef: WindowRef,
    protected csAgentAuthService: CsAgentAuthService
  ) {
    super(
      authService,
      authStorageService,
      oAuthLibWrapperService,
      routingService,
      occEndpoints,
      globalMessageService,
      authRedirectService,
      cookieService,
      router,
      winRef
    )
  }

  public shouldAddAuthorizationHeader(request: HttpRequest<any>): boolean {
    return super.shouldAddAuthorizationHeader(request) || this.isCSAgentTokenRequest(request)
  }

  /**
   * @override
   *
   * Checks if particular request should be handled by this service.
   */
  public shouldCatchError(request: HttpRequest<any>): boolean {
    return super.shouldCatchError(request) || this.isCSAgentTokenRequest(request)
  }

  /**
   * @override
   *
   * Adds `Authorization` header to occ and CS agent requests.
   * For CS agent requests also removes the `cx-use-csagent-token` header (to avoid problems with CORS).
   */
  public alterRequest(request: HttpRequest<any>, token?: AuthToken): HttpRequest<any> {
    const hasAuthorizationHeader = !!this.getAuthorizationHeader(request)
    const isCSAgentRequest = this.isCSAgentTokenRequest(request)

    let req = super.alterRequest(request)
    if (!hasAuthorizationHeader && isCSAgentRequest) {
      const asmAccessToken = JSON.parse(
        this.winRef.nativeWindow.localStorage.getItem(EbLocalStorageKey.AUTH_KEY)
      )
      req = request.clone({
        setHeaders: {
          ...this.createAuthorizationHeader(asmAccessToken?.token)
        }
      })
      return InterceptorUtil.removeHeader(USE_CUSTOMER_SUPPORT_AGENT_TOKEN, req)
    }
    return req
  }

  protected isCSAgentTokenRequest(request: HttpRequest<any>): boolean {
    const isRequestWithCSAgentToken = InterceptorUtil.getInterceptorParam(
      USE_CUSTOMER_SUPPORT_AGENT_TOKEN,
      request.headers
    )
    return Boolean(isRequestWithCSAgentToken)
  }

  /**
   * @override
   *
   * On backend errors indicating expired `refresh_token` we need to logout
   * currently logged in user and CS agent.
   */
  public handleExpiredRefreshToken(): void {
    this.csAgentAuthService
      .isCustomerSupportAgentLoggedIn()
      .pipe(take(1))
      .subscribe(csAgentLoggedIn => {
        if (csAgentLoggedIn) {
          this.authService.setLogoutProgress(true)
          this.csAgentAuthService.logoutCustomerSupportAgent()
          this.globalMessageService.add(
            {
              key: 'asm.csagentTokenExpired'
            },
            GlobalMessageType.MSG_TYPE_ERROR
          )
        } else {
          super.handleExpiredRefreshToken()
        }
      })
  }
}
